import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  applyChannelProfile,
  Channel,
  ChannelProfile,
  listChannelProfiles,
  listChannels,
} from '../api/channels';
import { ChannelsSectionNav } from '../components/channels/ChannelsSectionNav';
import { borderRadius, colorExtensions, colors, shadows, spacing, typography, typographyFlat } from '../styles/tokens';
import { formatRelativeTime } from '../utils/datetime';

const ChannelsPage: React.FC = () => {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [profiles, setProfiles] = useState<ChannelProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [applyingProfile, setApplyingProfile] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileResult, setProfileResult] = useState<string | null>(null);
  const [selectedProfileId, setSelectedProfileId] = useState<string>('live-dual-syncbase-authority');
  const [enableManapoolInboundReconciliation, setEnableManapoolInboundReconciliation] = useState(false);
  const [lastProfilePreviewKey, setLastProfilePreviewKey] = useState<string | null>(null);

  const fetchChannels = useCallback(async (silent: boolean = false) => {
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const data = await listChannels();
      setChannels(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load channels');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void fetchChannels(false);
  }, [fetchChannels]);

  const fetchProfiles = useCallback(async () => {
    setProfilesLoading(true);
    setProfileError(null);
    try {
      const data = await listChannelProfiles();
      setProfiles(Array.isArray(data) ? data : []);
      if (Array.isArray(data) && data.length > 0) {
        setSelectedProfileId((current) => {
          const hasCurrent = data.some((profile) => profile.id === current);
          return hasCurrent ? current : data[0].id;
        });
      }
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Failed to load channel modes');
    } finally {
      setProfilesLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchProfiles();
  }, [fetchProfiles]);

  useEffect(() => {
    const anySyncing = channels.some((channel) => channel.status === 'syncing');
    const intervalMs = anySyncing ? 3000 : 30000;
    const intervalId = window.setInterval(() => {
      void fetchChannels(true);
    }, intervalMs);

    return () => window.clearInterval(intervalId);
  }, [channels, fetchChannels]);

  const getStatusLabel = (status?: Channel['status']) => {
    if (!status) return 'Disconnected';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusColor = (status?: Channel['status']) => {
    if (status === 'connected' || status === 'active') return colorExtensions.successDark;
    return colorExtensions.errorDark;
  };

  const selectedProfile = profiles.find((profile) => profile.id === selectedProfileId) || null;
  const profileRunKey = `${selectedProfileId}:${enableManapoolInboundReconciliation ? 'inbound-on' : 'inbound-off'}`;
  const profilePreviewReady = lastProfilePreviewKey === profileRunKey;

  const handleApplyProfile = useCallback(async (dryRun: boolean = false) => {
    if (!selectedProfileId) {
      return;
    }
    const runKey = `${selectedProfileId}:${enableManapoolInboundReconciliation ? 'inbound-on' : 'inbound-off'}`;
    if (!dryRun) {
      if (lastProfilePreviewKey !== runKey) {
        setProfileError('Preview this mode first before applying.');
        return;
      }
      const shouldProceed = window.confirm(
        `Apply channel mode "${selectedProfileId}" now?\n\nThis changes sync_enabled / inbound / outbound / webhook flags across channels.`,
      );
      if (!shouldProceed) {
        return;
      }
    }
    setApplyingProfile(true);
    setProfileError(null);
    setProfileResult(null);
    try {
      const response = await applyChannelProfile(selectedProfileId, {
        dry_run: dryRun,
        enable_manapool_inbound_reconciliation: enableManapoolInboundReconciliation,
      });
      setProfileResult(
        dryRun
          ? `Preview: ${response.message}`
          : response.message,
      );
      if (dryRun) {
        setLastProfilePreviewKey(runKey);
      }
      if (!dryRun) {
        setChannels(Array.isArray(response.channels) ? response.channels : []);
      }
      await fetchChannels(true);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Failed to apply channel mode');
    } finally {
      setApplyingProfile(false);
    }
  }, [enableManapoolInboundReconciliation, fetchChannels, lastProfilePreviewKey, selectedProfileId]);

  const formatLastSync = (timestamp?: string | null) =>
    formatRelativeTime(timestamp, { fallback: 'Never', maxDays: Number.POSITIVE_INFINITY });

  return (
    <div style={styles.container}>
      <div>
        <h1 style={styles.title}>Channels</h1>
        <p style={styles.subtitle}>Combined status view for all channel syncs. Open a channel for full health details.</p>
        <ChannelsSectionNav />
      </div>

      <div style={styles.modeCard}>
        <div style={styles.modeHeader}>
          <h2 style={styles.modeTitle}>Channel Mode</h2>
          <span style={styles.modeHint}>Apply a predefined operational posture.</span>
        </div>

        {profilesLoading ? (
          <div style={styles.loadingText}>Loading channel modes...</div>
        ) : (
          <>
            <label style={styles.modeLabel}>
              Mode
              <select
                value={selectedProfileId}
                onChange={(event) => {
                  setSelectedProfileId(event.target.value);
                  setLastProfilePreviewKey(null);
                }}
                style={styles.modeSelect}
              >
                {profiles.map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.display_name}
                  </option>
                ))}
              </select>
            </label>

            {selectedProfile && (
              <div style={styles.modeDescription}>{selectedProfile.description}</div>
            )}

            {selectedProfileId === 'live-dual-syncbase-authority' && (
              <label style={styles.modeCheckboxLabel}>
                <input
                  type="checkbox"
                  checked={enableManapoolInboundReconciliation}
                  onChange={(event) => {
                    setEnableManapoolInboundReconciliation(event.target.checked);
                    setLastProfilePreviewKey(null);
                  }}
                />
                Enable Manapool inbound reconciliation pull
              </label>
            )}

            <div style={styles.modeActions}>
              <button
                type="button"
                onClick={() => handleApplyProfile(true)}
                disabled={applyingProfile || profilesLoading || !selectedProfileId}
                style={styles.secondaryButton}
              >
                {applyingProfile ? 'Working...' : 'Preview (dry run)'}
              </button>
              <button
                type="button"
                onClick={() => handleApplyProfile(false)}
                disabled={applyingProfile || profilesLoading || !selectedProfileId || !profilePreviewReady}
                style={{
                  ...styles.primaryButton,
                  ...(applyingProfile || !profilePreviewReady ? styles.primaryButtonDisabled : {}),
                }}
                title={!profilePreviewReady ? 'Preview this mode first' : undefined}
              >
                {applyingProfile ? 'Applying...' : 'Apply Mode'}
              </button>
            </div>
            <div style={styles.modeHint}>
              {profilePreviewReady
                ? `Preview ready for ${profileRunKey}. Apply unlocked.`
                : `Preview ${profileRunKey} before applying.`}
            </div>

            {profileError && <div style={styles.errorBox}>{profileError}</div>}
            {profileResult && <div style={styles.successBox}>{profileResult}</div>}
          </>
        )}
      </div>

      {error && (
        <div style={styles.errorBox}>
          {error}
          <div style={{ marginTop: spacing.sm }}>
            <button type="button" style={styles.secondaryButton} onClick={() => void fetchChannels(false)}>
              Retry
            </button>
          </div>
        </div>
      )}

      {loading && channels.length === 0 ? (
        <div style={styles.loadingText}>Loading channels...</div>
      ) : (
        <div style={styles.grid}>
          {channels.map((channel) => (
            <Link key={channel.id} to={`/channels/${channel.name}`} style={styles.cardLink}>
              <div style={styles.card}>
                <div style={styles.cardHeaderRow}>
                  <h3 style={styles.cardTitle}>{channel.display_name}</h3>
                  <span style={{ ...styles.statusDot, backgroundColor: getStatusColor(channel.status) }} />
                </div>
                <div style={{ ...styles.statusText, color: getStatusColor(channel.status) }}>
                  {getStatusLabel(channel.status)}
                </div>
                <div style={styles.metaText}>Last sync: {formatLastSync(channel.last_sync_at)}</div>
              </div>
            </Link>
          ))}
          {!loading && channels.length === 0 && !error && (
            <div style={styles.loadingText}>No channels returned from the API.</div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: spacing.lg,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.lg,
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    margin: `${spacing.xs} 0 0 0`,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  modeCard: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    backgroundColor: colors.background,
    boxShadow: shadows.sm,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.md,
  },
  modeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.sm,
  },
  modeTitle: {
    margin: 0,
    color: colors.text,
    fontSize: typography.h3.fontSize,
    fontWeight: typography.h3.fontWeight,
  },
  modeHint: {
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  modeLabel: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.xs,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  modeSelect: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background,
    color: colors.text,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typographyFlat.fontSize.md,
  },
  modeDescription: {
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  modeCheckboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  modeActions: {
    display: 'flex',
    gap: spacing.sm,
    flexWrap: 'wrap' as const,
  },
  primaryButton: {
    border: 'none',
    borderRadius: borderRadius.md,
    backgroundColor: colors.primary,
    color: colors.background,
    padding: `${spacing.xs} ${spacing.md}`,
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.semibold,
    cursor: 'pointer',
  },
  primaryButtonDisabled: {
    opacity: 0.65,
    cursor: 'not-allowed',
  },
  secondaryButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background,
    color: colors.text,
    padding: `${spacing.xs} ${spacing.md}`,
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.medium,
    cursor: 'pointer',
  },
  successBox: {
    padding: spacing.md,
    backgroundColor: `${colorExtensions.successDark}10`,
    color: colorExtensions.successDark,
    borderRadius: borderRadius.md,
  },
  errorBox: {
    padding: spacing.md,
    backgroundColor: `${colors.error}10`,
    color: colors.error,
    borderRadius: borderRadius.md,
  },
  loadingText: {
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: spacing.md,
  },
  cardLink: {
    textDecoration: 'none',
  },
  card: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    backgroundColor: colors.background,
    boxShadow: shadows.sm,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.sm,
  },
  cardHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardTitle: {
    margin: 0,
    color: colors.text,
    fontSize: typography.h3.fontSize,
    fontWeight: typography.h3.fontWeight,
  },
  statusDot: {
    width: '12px',
    height: '12px',
    borderRadius: borderRadius.full,
    flexShrink: 0,
  },
  statusText: {
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  metaText: {
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
};

export default ChannelsPage;
